import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  MAX_FILE_SIZE,
  ACCEPT_ATTR,
  isAllowedFile,
  formatBytes,
} from '../utils/presentationValidation'
import { Button } from './Button'
import { FileDropZone, Modal } from '@proaxiv/ui'

// ── Upload icon ────────────────────────────────────────────────────────────────

const UploadIcon = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    className="text-pax-gray"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
)

const CloseIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
)

// ── Component ──────────────────────────────────────────────────────────────────

interface Props {
  registrationId: string
  participantName: string
  onDone: () => void
  onCancel: () => void
  /** When provided, called with the selected file instead of storing in IndexedDB. */
  onUpload?: (participantId: string, file: File) => Promise<void>
}

/**
 * Modal for the event organizer to upload a presentation on behalf of a
 * Speaker/Host participant (backup procedure).
 * On confirm, the backend stores the file and automatically accepts the
 * presentation on the organizer's behalf — no separate review step is needed.
 */
export function PresentationUploadModal({
  registrationId,
  participantName,
  onDone,
  onCancel,
  onUpload,
}: Props) {
  const { t } = useTranslation()

  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)

  function handleFileSelected(file: File) {
    setError(null)

    if (file.size > MAX_FILE_SIZE) {
      setError(t('pres_err_size'))
      setSelectedFile(null)
      return
    }
    if (!isAllowedFile(file)) {
      setError(t('pres_err_type'))
      setSelectedFile(null)
      return
    }
    setSelectedFile(file)
  }

  async function handleConfirm() {
    if (!selectedFile || uploading) return
    setUploading(true)
    try {
      if (onUpload) {
        await onUpload(registrationId, selectedFile)
      }
      onDone()
    } catch {
      setError(t('pres_err_upload'))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Modal labelledBy="pres-upload-modal-title">
      <div className="border-pax-purple/50 relative w-full max-w-md rounded-2xl border bg-white p-6 shadow-2xl ring-1 ring-black/5">
        <button
          type="button"
          onClick={onCancel}
          className="text-pax-gray hover:text-pax-black absolute top-4 right-4 cursor-pointer rounded-lg p-1 transition-colors focus-visible:outline-none"
          aria-label="Close"
        >
          <CloseIcon />
        </button>

        <h2 id="pres-upload-modal-title" className="text-pax-black mb-1 pr-8 text-xl font-bold">
          {t('pres_upload_title')}
        </h2>
        <p className="text-pax-gray mb-5 text-sm">
          {t('pres_upload_subtitle', { name: participantName })}
        </p>

        {/* Drop zone / file picker — shared component, styled to match this modal's existing look */}
        <FileDropZone
          accept={ACCEPT_ATTR}
          disabled={uploading}
          onFileSelected={handleFileSelected}
          icon={<UploadIcon />}
          uploadLabel={
            <>
              <span className="text-pax-black text-sm font-medium">{t('pres_upload_choose')}</span>
              <span className="text-pax-gray text-xs">{t('pres_upload_constraints')}</span>
            </>
          }
          className="mb-1"
          dropzoneClassName="border-pax-purple/40 hover:border-pax-blue hover:bg-pax-light-purple/30 flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors focus:outline-none focus-visible:ring-2"
          dropzoneActiveClassName="border-pax-blue bg-pax-light-purple/30"
        />

        {/* Selected file info */}
        {selectedFile && !error && (
          <p className="text-pax-green mb-4 text-sm font-medium">
            {selectedFile.name} ({formatBytes(selectedFile.size)})
          </p>
        )}

        {/* Error */}
        {error && (
          <p role="alert" className="text-pax-red mb-4 text-sm font-medium">
            {error}
          </p>
        )}

        {!selectedFile && !error && <div className="mb-4" />}

        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3">
          <Button variant="secondary" onClick={onCancel}>
            {t('btn_cancel')}
          </Button>
          <Button
            variant="primary"
            onClick={() => void handleConfirm()}
            disabled={!selectedFile || uploading}
          >
            {t('pres_upload_confirm')}
          </Button>
        </div>
      </div>
    </Modal>
  )
}
